import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import type { Question as QuestionJSON } from 'schema-dts';
import { useConfigure, useInstantSearch } from 'react-instantsearch-core';
import type { SearchResults } from 'algoliasearch-helper';
import type { FAQDoc } from '../../../../general/Input';
import type { BankDetail, Category as CategoryType, CustomerService } from '../../../types';
import Category from './Category/Category';
import Translation from '../../../../../general/Translation/Translation';
import LdJson from '../../../../../general/LdJson/LdJson';
import { addDeviceTag } from '../../../../general/Analytics/Analytics';
import useHasAnalytics from '../../../../general/Analytics/useHasAnalytics';

export type ResultProps = {
    bankDetail:BankDetail;
    categories: CategoryType[];
    contactInfo: CustomerService;
    faqUri: string;
};

const Results: React.FC<ResultProps> = ({ bankDetail, categories, contactInfo, faqUri }) => {
    const { hash } = useLocation();
    const [selectedQuestionId, setSelectedQuestionId] = useState<string>('');
    const analytics = useHasAnalytics();

    useConfigure({
        analytics,
        analyticsTags: addDeviceTag(['page:faq']),
        clickAnalytics: analytics,
        getRankingInfo: true,
        hitsPerPage: 100,
        ruleContexts: addDeviceTag(['page_faq'], true),
    });

    const { results: untypedResults } = useInstantSearch();
    const results = untypedResults as SearchResults<FAQDoc>;

    const hashID = hash.indexOf('question') === 1 ? hash.substring(9) : '0';

    useEffect(() => {
        setSelectedQuestionId(hashID);
    }, [hashID]);

    const { hits, query } = results;

    const hitsByCategory = hits.reduce<Record<string, FAQDoc[]>>((current, hit) => {
        const currentHits = current[hit.categoryID] ?? [];
        currentHits.push(hit);

        return {
            ...current,
            [hit.categoryID]: currentHits,
        };
    }, {});

    const categoriesById = categories.map((category) => ({
        category,
        hits: hitsByCategory[category.id] ?? [],
    }));

    return (
        <>
            <LdJson jsonData={{
                '@type': 'FAQPage',
                mainEntity: [
                    ...hits.map<QuestionJSON>((doc) => ({
                        '@type': 'Question',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: doc.answer,
                        },
                        name: doc.question,
                    })),
                ],
            }} />
            <div className="is-col-3 is-col-2-lg">
                {hits.length
                    ? (categoriesById.map(({ category, hits: faqs }) => (
                        faqs.length > 0 && (
                            <Category
                                key={category.name}
                                category={category}
                                faqs={faqs}
                                faqUri={faqUri}
                                selectedQuestionId={selectedQuestionId}
                                isSearched={!!query}
                                bankDetail={bankDetail}
                                contactInfo={contactInfo}
                            />
                        )
                    )))
                    : (
                        <div className="block">
                            <div className="content">
                                <h3>
                                    <Translation pageString='SAresults' stringId='geen_resultaten' />
                                </h3>
                            </div>
                        </div>
                    )}
            </div>
        </>
    );
};

export default Results;
